import { createContext, useContext, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { cn } from "../lib/utils";

type ToastVariant = "default" | "success" | "warning" | "destructive";

interface ToastItem {
  id: number;
  message: string;
  variant?: ToastVariant;
  duration?: number;
  title?: string;
  action?: { label: string; onClick: () => void };
}

interface ToastContextValue {
  toast: (message: string, options?: { variant?: ToastVariant; duration?: number; title?: string; action?: { label: string; onClick: () => void } }) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const counter = useRef(0);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback(
    (message: string, options?: { variant?: ToastVariant; duration?: number; title?: string; action?: { label: string; onClick: () => void } }) => {
      const id = ++counter.current;
      const duration = options?.duration ?? 3500;
      setToasts((prev) => [...prev, { id, message, variant: options?.variant, duration, title: options?.title, action: options?.action }]);
      setTimeout(() => dismiss(id), duration);
    },
    [dismiss]
  );

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      {createPortal(
        <div className="toaster" aria-live="polite" aria-atomic="true">
          {toasts.map((t) => (
            <div
              key={t.id}
              className={cn("toast", t.variant && `toast-${t.variant}`)}
              role="alert"
            >
              <div className="toast-body">
                {t.title && <span className="toast-title">{t.title}</span>}
                <span className="toast-msg">{t.message}</span>
              </div>
              <div className="toast-actions">
                {t.action && (
                  <button
                    className="toast-action"
                    onClick={() => { t.action!.onClick(); dismiss(t.id); }}
                  >
                    {t.action.label}
                  </button>
                )}
                <button
                  className="toast-close"
                  onClick={() => dismiss(t.id)}
                  aria-label="Dismiss"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside <ToastProvider>");
  return ctx;
}
